import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, ArrowLeft, Plus, Trash2, Layers, ArrowUp, ArrowDown, Eye, Download, CheckCircle2, Award, Briefcase, History, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";
import { computeAssembly } from "@/utils/assemblyCalc";
import TreatmentField from "@/components/TreatmentField";
import OverheadsTable from "@/components/OverheadsTable";
import AssemblyLiveCalcPanel from "@/components/AssemblyLiveCalcPanel";
import MiscFilesPanel from "@/components/MiscFilesPanel";
import AssemblyCompareView from "@/components/AssemblyCompareView";
import ScreenshotUpload from "@/components/ScreenshotUpload";
import DecimalInput from "@/components/DecimalInput";
import GroupedNumberInput from "@/components/GroupedNumberInput";
import SearchableSelect from "@/components/SearchableSelect";
import AssemblyPdfDownloader from "@/components/AssemblyPdfDownloader";

const INITIAL = {
  assembly_name: "", part_number: "", drawing_number: "",
  customer_id: "", end_customer: "", model_name: "", application: "",
  customer_due_date: "", quote_validity_date: "", remarks: "",
  components: [],
  assembly_processes: [], assembly_bop_items: [],
  assembly_surface_treatment: { mode: "per_piece", rate: 0, notes: "" },
  assembly_heat_treatment:    { mode: "per_piece", rate: 0, notes: "" },
  assembly_tool_maintenance_pct: 0,
  // R48 — lumpsum mode
  assembly_tool_maintenance_overridden: false,
  assembly_tool_maintenance_fixed: 0,
  assembly_development_cost: 0,
  assembly_overheads: [],
  // R46 — Section F: customer markups / discounts
  assembly_custom_adjustments: [],
  assembly_custom_adjustments_visible_in_quote: false,
  unit_id: "",
  is_awarded: false,
  yoy_reduction_pct: 0,
  project_life_years: 1,
  // Project Scope renders the schedule at all times, so seed with Y1.
  volumes: [{ year_number: 1, year_label: "", annual_volume_pcs: 0, monthly_volume_pcs: 0 }],
  // Round 17 — YoY apply-years cap (null = no cap)
  yoy_apply_years: null,
};

export default function AssemblyForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const { user, hasRole, hasPermission } = useAuth();
  const hideMargin = user ? !user.view_margin : true;
  const canApprove = hasPermission("assembly.approve");
  const canAward = hasPermission("assembly.award");

  const [formData, setFormData] = useState(INITIAL);
  const [customers, setCustomers] = useState([]);
  const [units, setUnits] = useState([]);
  const [npdOptions, setNpdOptions] = useState([]);
  const [asmOptions, setAsmOptions] = useState([]); // other assemblies, selectable as nested sub-parts
  const [componentNpds, setComponentNpds] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [pdfAsmId, setPdfAsmId] = useState(null); // triggers the combined assembly+parts PDF
  const [showYoy, setShowYoy] = useState(false);
  const [rounds, setRounds] = useState([]);
  const [comparingRound, setComparingRound] = useState(null); // shape: null | { left: roundId | "_current_", right: roundId | "_current_" }
  const [savingRound, setSavingRound] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    if (user?.role === "admin") {
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
    }
    // FIX: the /npd list defaults to exclude_assembly_children=true, which hides any
    // NPD already linked into an assembly — so a saved sub-part vanished from this
    // picker and the dropdown fell back to "Select NPD…". Pass false to list all.
    api.get("/npd", { params: { limit: 500, exclude_assembly_children: false } }).then((r) => setNpdOptions(r.data.npds || [])).catch(() => {});
    // Other assemblies can be nested as sub-parts (cost-only rollup).
    api.get("/assemblies", { params: { limit: 500 } }).then((r) => setAsmOptions(r.data.assemblies || [])).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    if (!isEdit) {
      // New assembly — prefill default overheads AND processes from Settings (same
      // master as NPD costings), so B'. Assembly Process Cost isn't empty.
      api.get("/settings/public").then((r) => {
        try {
          const oh = JSON.parse(r.data.default_overheads || "[]");
          const procs = JSON.parse(r.data.default_processes || "[]");
          setFormData((p) => ({ ...p, assembly_overheads: oh, assembly_processes: procs }));
        } catch {
          // ignore
        }
      });
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/assemblies/${id}`);
        setMeta({
          assembly_number: r.data.assembly_number,
          status: r.data.status,
          approved_for_export: r.data.approved_for_export,
          approved_at: r.data.approved_at,
          approved_by_name: r.data.approved_by_name,
          is_awarded: r.data.is_awarded,
          awarded_at: r.data.awarded_at,
          awarded_by: r.data.awarded_by,
          outcome: r.data.outcome || "pending",
          outcome_at: r.data.outcome_at,
          outcome_by: r.data.outcome_by,
        });
        const merged = { ...INITIAL, ...r.data };
        // Reshape volumes to project_life_years; year_label intentionally blank
        // — the user types SOP year into Y1 and Y2..Yn auto-fill from there.
        const life = Number(merged.project_life_years) || 1;
        if (life > 0 && (!merged.volumes || merged.volumes.length !== life)) {
          const reshaped = [];
          for (let i = 1; i <= life; i++) {
            const existing = (merged.volumes || []).find((v) => Number(v.year_number) === i);
            reshaped.push(existing || { year_number: i, year_label: "", annual_volume_pcs: 0, monthly_volume_pcs: 0 });
          }
          merged.volumes = reshaped;
        }
        setFormData(merged);
        // Sync YoY toggle after data lands (a useEffect on [id] alone races setFormData).
        setShowYoy(
          Number(merged.yoy_reduction_pct || 0) > 0 ||
          (merged.yoy_apply_years != null && Number(merged.yoy_apply_years) > 0)
        );
      } catch (e) {
        toast.error("Linked assembly was deleted or could not be loaded");
        nav("/assemblies");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  // Fetch full NPD docs for components whenever components change
  useEffect(() => {
    const ids = (formData.components || []).map((c) => c.npd_id).filter(Boolean);
    if (ids.length === 0) { setComponentNpds([]); return; }
    Promise.all(ids.map((nid) => {
      if (String(nid).startsWith("asm_")) {
        // Nested assembly sub-part — pull its computed Grand Total for the live line.
        return api.get(`/assemblies/${nid}`).then((r) => ({
          npd_id: nid,
          _is_assembly: true,
          grand_total: r.data.grand_total,
          development_cost_total: r.data.development_cost_total,
          npd_number: r.data.assembly_number,
          part_name: r.data.assembly_name,
          part_number: "",
        })).catch(() => null);
      }
      return api.get(`/npd/${nid}`).then((r) => r.data).catch(() => null);
    })).then((arr) => setComponentNpds(arr.filter(Boolean)));
  }, [formData.components]);

  // YoY toggle sync now happens inside the load handler above — the standalone
  // useEffect on [id] raced setFormData, leaving the box unchecked on edit.

  const calc = useMemo(() => computeAssembly(formData, componentNpds), [formData, componentNpds]);

  // Safety net for the sub-part picker: always include the NPDs already linked as
  // components (componentNpds), even if the list API filtered them out or is still
  // loading. Deduped by npd_id so the selected part always renders.
  const npdPickerOptions = useMemo(() => {
    const byId = {};
    (npdOptions || []).forEach((n) => { if (n && n.npd_id) byId[n.npd_id] = n; });
    (componentNpds || []).forEach((n) => { if (n && n.npd_id && !byId[n.npd_id]) byId[n.npd_id] = n; });
    return Object.values(byId).sort((a, b) => String(a.npd_number || "").localeCompare(String(b.npd_number || "")));
  }, [npdOptions, componentNpds]);
  // Options for the searchable sub-part picker — lead with the PART NAME (so the selected
  // value stays readable in the narrow box) then the NPD number. Search matches the whole label.
  // Nested-assembly options (exclude THIS assembly to prevent self-reference).
  const asmSelectOptions = useMemo(
    () => (asmOptions || [])
      .filter((a) => a && a.assembly_id && a.assembly_id !== id)
      .map((a) => ({
        value: a.assembly_id,
        label: `[ASM] ${a.assembly_name || "(unnamed)"} — ${a.assembly_number}`,
      })),
    [asmOptions, id]
  );
  const npdSelectOptions = useMemo(
    () => [
      ...npdPickerOptions.map((n) => ({
        value: n.npd_id,
        label: `${n.part_name || "(unnamed)"} — ${n.npd_number}${n.part_number ? ` (${n.part_number})` : ""}`,
      })),
      ...asmSelectOptions,
    ],
    [npdPickerOptions, asmSelectOptions]
  );
  const threshold = Number(settings.margin_red_flag_threshold_pct || 15);

  const update = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const addComponent = () =>
    update("components", [...(formData.components || []), { npd_id: "", qty_per_assembly: 1, notes: "" }]);
  const removeComponent = (idx) =>
    update("components", (formData.components || []).filter((_, i) => i !== idx));
  const updateComponent = (idx, patch) =>
    update("components", (formData.components || []).map((c, i) => i === idx ? { ...c, ...patch } : c));
  const moveComponent = (idx, dir) => {
    const arr = [...(formData.components || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    update("components", arr);
  };

  const save = async () => {
    if (!formData.customer_id) { toast.error("Select a customer"); return; }
    if (!formData.assembly_name || !formData.part_number) { toast.error("Assembly name and part number required"); return; }
    if (!isEdit && user?.role === "admin" && !formData.unit_id) { toast.error("Pick a unit"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/assemblies/${id}`, formData);
        toast.success("Assembly saved");
      } else {
        const r = await api.post("/assemblies", formData);
        toast.success(`Created ${r.data.assembly_number}`);
        nav(`/assemblies/${r.data.assembly_id}`, { replace: true });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!window.confirm(meta?.approved_for_export ? "Revoke approval?" : `Approve ${meta?.assembly_number} for Excel export?`)) return;
    try {
      const r = await api.post(`/assemblies/${id}/approve`, { approved: !meta?.approved_for_export });
      setMeta((p) => ({
        ...p, approved_for_export: r.data.approved_for_export,
        approved_at: r.data.approved_for_export ? (r.data.approved_at || new Date().toISOString()) : null,
        approved_by_name: r.data.approved_for_export ? (r.data.approved_by_name || user?.name || p?.approved_by_name) : null,
      }));
      toast.success(r.data.approved_for_export ? "Approved" : "Approval revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get(`/assemblies/${id}/export.xlsx?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${meta?.assembly_number || "ASM"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel export failed");
    }
  };

  const setOutcome = async (newOutcome) => {
    if ((meta?.outcome || "pending") === newOutcome) return;
    const confirmMsg =
      newOutcome === "won"
        ? `Mark ${meta?.assembly_number} as WON?${!meta?.approved_for_export ? "\n\nNote: this assembly is not yet Approved for Export." : ""}`
        : newOutcome === "lost"
        ? `Mark ${meta?.assembly_number} as LOST? Customer chose another supplier. Component NPDs are NOT cascaded — mark them individually if needed.`
        : newOutcome === "dropped"
        ? `Mark ${meta?.assembly_number} as DROPPED? We chose not to pursue, or customer abandoned. Component NPDs are NOT cascaded.`
        : `Reopen ${meta?.assembly_number} (set back to Pending)?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await api.post(`/assemblies/${id}/set-outcome`, { outcome: newOutcome });
      setMeta((p) => ({
        ...p,
        outcome: r.data.outcome,
        outcome_at: r.data.outcome_at,
        outcome_by: r.data.outcome_by,
        is_awarded: r.data.is_awarded,
        awarded_at: r.data.is_awarded ? r.data.outcome_at : null,
        awarded_by: r.data.is_awarded ? r.data.outcome_by : null,
      }));
      toast.success(newOutcome === "pending" ? "Reopened" : `Marked ${newOutcome.toUpperCase()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const loadRounds = async () => {
    if (!isEdit) return;
    try {
      const r = await api.get(`/assemblies/${id}/rounds`);
      setRounds(r.data.rounds || []);
    } catch (e) {
      // silent — engineer without access will 403
    }
  };

  useEffect(() => { loadRounds(); /* eslint-disable-next-line */ }, [id]);

  const saveRound = async () => {
    if (!isEdit) return;
    // R47 — Two-step atomic save: (1) persist current form data, (2) snapshot
    // the just-saved doc as a new round. Without step (1), unsaved form edits
    // are silently lost. If the assembly was approved, approval is auto-revoked.
    if (!formData.customer_id) { toast.error("Select a customer"); return; }
    if (!formData.assembly_name || !formData.part_number) { toast.error("Assembly name and part number required"); return; }

    const notes = window.prompt(
      `Save Round N${(meta?.current_round || 0) + 1} for ${meta?.assembly_number}?\n\n` +
      `This SAVES your current form changes AND creates a snapshot of the assembly + every linked component NPD. Component NPDs' OWN round counters are NOT bumped (components are shared across assemblies).\n\n` +
      `If this assembly was Approved, the approval will be revoked — re-approval needed for the new round.\n\n` +
      `Notes for this round (optional):`,
      ""
    );
    if (notes === null) return;
    setSavingRound(true);
    try {
      // Step 1 — Persist current form data first
      await api.put(`/assemblies/${id}`, formData);

      // Step 2 — Snapshot the just-saved doc as a new round
      const r = await api.post(`/assemblies/${id}/save-round`, { notes });
      if (r.data?.approval_revoked) {
        toast.success(`Saved ${r.data.round_label} (${r.data.component_count} components). Previous approval revoked — needs re-approval for export.`);
      } else {
        toast.success(`Saved ${r.data.round_label} (${r.data.component_count} components captured)`);
      }
      setMeta((p) => ({
        ...p,
        current_round: r.data.current_round,
        approved_for_export: false,
        approved_by: null,
        approved_at: null,
      }));
      await loadRounds();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save round failed");
    } finally { setSavingRound(false); }
  };

  const updateProjectLife = (years) => {
    const y = Math.max(1, Math.min(20, Math.round(Number(years) || 1)));
    const currentVolumes = formData.volumes || [];
    const newVolumes = [];
    for (let i = 1; i <= y; i++) {
      const existing = currentVolumes.find((v) => Number(v.year_number) === i);
      newVolumes.push(existing || { year_number: i, year_label: "", annual_volume_pcs: 0, monthly_volume_pcs: 0 });
    }
    // If Y1 has a parseable SOP year, fill any still-blank later years.
    const sop = parseInt(String(newVolumes[0]?.year_label || "").trim(), 10);
    if (Number.isFinite(sop) && sop >= 1900 && sop <= 2200) {
      for (let i = 1; i < newVolumes.length; i++) {
        if (!newVolumes[i].year_label) newVolumes[i] = { ...newVolumes[i], year_label: String(sop + i) };
      }
    }
    setFormData((p) => ({ ...p, project_life_years: y, volumes: newVolumes }));
  };

  // When user fills Y1's year_label with a parseable year, auto-derive Y2..Yn
  // into rows the user hasn't manually touched. Manual overrides are preserved.
  const updateVolumeRow = (idx, patch) => {
    setFormData((p) => {
      const rows = (p.volumes || []).map((v, i) => {
        if (i !== idx) return v;
        const updated = { ...v, ...patch };
        if ("annual_volume_pcs" in patch) {
          updated.monthly_volume_pcs = Math.round(((patch.annual_volume_pcs || 0) / 12) * 100) / 100;
        } else if ("monthly_volume_pcs" in patch) {
          updated.annual_volume_pcs = Math.round((patch.monthly_volume_pcs || 0) * 12 * 100) / 100;
        }
        return updated;
      });
      if (idx === 0 && "year_label" in patch) {
        const sop = parseInt(String(patch.year_label).trim(), 10);
        if (Number.isFinite(sop) && sop >= 1900 && sop <= 2200) {
          for (let i = 1; i < rows.length; i++) {
            if (!rows[i].year_label) {
              rows[i] = { ...rows[i], year_label: String(sop + i) };
            }
          }
        }
      }
      return { ...p, volumes: rows };
    });
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto">
      {formData.project_id && (
        <div className="mb-3 text-xs text-slate-600 flex items-center gap-1.5" data-testid="assembly-project-breadcrumb">
          <Briefcase size={12} className="text-slate-400" />
          <span>Part of project</span>
          <Link to={`/projects/${formData.project_id}`} className="text-sky-600 hover:text-sky-700 font-mono">
            {formData.project_id}
          </Link>
          {formData.child_index ? <span className="text-slate-500">· A{formData.child_index}</span> : null}
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to="/assemblies" className="p-2 hover:bg-slate-100 rounded-md text-slate-500"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Layers size={20} className="text-orange-600" />
              {isEdit ? (formData.assembly_name || "Assembly") : "New Assembly"}
              {meta?.approved_for_export && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                  title={meta.approved_by_name ? `Approved by ${meta.approved_by_name}` : "Approved"}>
                  <CheckCircle2 size={12} /> Approved{meta.approved_at ? ` · ${new Date(meta.approved_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                </span>
              )}
              {meta?.outcome === "won" && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid="asm-outcome-badge-won">
                  <Award size={12} /> Won
                </span>
              )}
              {meta?.outcome === "lost" && (
                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded" data-testid="asm-outcome-badge-lost">Lost</span>
              )}
              {meta?.outcome === "dropped" && (
                <span className="text-xs bg-stone-200 text-stone-800 px-2 py-0.5 rounded" data-testid="asm-outcome-badge-dropped">Dropped</span>
              )}
            </h1>
            {meta && <div className="text-xs text-slate-500 font-mono mt-0.5">{meta.assembly_number}</div>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isEdit && (
            <>
              <button
                onClick={() => window.open(`/assemblies/${id}/preview`, "_blank")}
                className="px-3 py-2 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-1.5"
                data-testid="asm-preview-btn"
              >
                <Eye size={14} /> Preview
              </button>
              {canApprove && (() => {
                const needsShot = !meta?.approved_for_export && isEdit && !formData.assembly_drawing_screenshot_path;
                return (
                  <button
                    onClick={approve}
                    disabled={needsShot}
                    title={needsShot ? "Upload a drawing screenshot before approving" : ""}
                    className={`px-3 py-2 text-sm rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                      meta?.approved_for_export
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "border border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                    }`}
                    data-testid="asm-approve-btn"
                  >
                    <CheckCircle2 size={14} /> {meta?.approved_for_export ? "✓ Approved" : "Approve"}
                  </button>
                );
              })()}
              {canAward && (() => {
                const o = meta?.outcome || "pending";
                return (
                  <div className="inline-flex items-stretch rounded-md overflow-hidden border border-slate-300" data-testid="asm-outcome-btn-group">
                    <button type="button" onClick={() => setOutcome("won")} className={`px-2.5 py-2 text-xs font-medium flex items-center gap-1 border-r border-slate-300 ${o === "won" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 hover:bg-emerald-50"}`} title="Mark as Won" data-testid="asm-outcome-won-btn">
                      <Award size={12} /> Won
                    </button>
                    <button type="button" onClick={() => setOutcome("lost")} className={`px-2.5 py-2 text-xs font-medium border-r border-slate-300 ${o === "lost" ? "bg-red-600 text-white" : "bg-white text-red-700 hover:bg-red-50"}`} title="Mark as Lost" data-testid="asm-outcome-lost-btn">
                      Lost
                    </button>
                    <button type="button" onClick={() => setOutcome("dropped")} className={`px-2.5 py-2 text-xs font-medium ${o === "dropped" ? "bg-stone-600 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`} title="Mark as Dropped" data-testid="asm-outcome-dropped-btn">
                      Dropped
                    </button>
                    {o !== "pending" && (
                      <button type="button" onClick={() => setOutcome("pending")} className="px-2.5 py-2 text-xs font-medium border-l border-slate-300 bg-white text-slate-600 hover:bg-slate-100" title="Reopen" data-testid="asm-outcome-reopen-btn">
                        ↺ Reopen
                      </button>
                    )}
                  </div>
                );
              })()}
              {meta?.approved_for_export && (
                <button
                  onClick={exportExcel}
                  className="px-3 py-2 text-sm bg-slate-900 hover:bg-slate-800 text-white rounded-md flex items-center gap-1.5"
                  data-testid="asm-excel-btn"
                >
                  <Download size={14} /> Excel
                </button>
              )}
              {meta?.approved_for_export && (
                <button
                  onClick={() => setPdfAsmId(id)}
                  disabled={!!pdfAsmId}
                  className="px-3 py-2 text-sm bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-md flex items-center gap-1.5"
                  data-testid="asm-pdf-btn"
                  title="Download the assembly quote with every sub-part quote appended into one PDF"
                >
                  <Download size={14} /> {pdfAsmId ? "Preparing…" : "PDF (with parts)"}
                </button>
              )}
            </>
          )}
          {/* Single Draft button (like NPD costings) — saves progress; screenshot is required at Approve. */}
          <button
            onClick={save}
            disabled={saving}
            data-testid="assembly-draft"
            className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
            title="Save your progress as a draft"
          >
            <Save size={14} /> {saving ? "Saving…" : "Draft"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
        <div className="space-y-4 min-w-0">
          {/* R29 Phase B — save-gate banner */}
          {isEdit && !formData.assembly_drawing_screenshot_path && (
            <div
              className="bg-amber-50 border border-amber-300 rounded-md p-3 flex items-start gap-2 text-sm text-amber-900"
              data-testid="screenshot-required-banner"
            >
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Drawing Screenshot required to Approve.</strong>{" "}
                You can save a Draft without it, but upload a JPG or PNG (max 2 MB) below before approving — it gets embedded inside the Section A panel of the customer quote sheet.
              </div>
            </div>
          )}

          <Section title="Assembly Identity">
            <Field label="Customer *">
              <select value={formData.customer_id} onChange={(e) => update("customer_id", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white">
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="End Customer"><input value={formData.end_customer || ""} onChange={(e) => update("end_customer", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <Field label="Assembly Name *"><input value={formData.assembly_name} onChange={(e) => update("assembly_name", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <Field label="Assembly Part Number *"><input value={formData.part_number} onChange={(e) => update("part_number", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <Field label="Drawing No."><input value={formData.drawing_number || ""} onChange={(e) => update("drawing_number", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <Field label="Model Name"><input value={formData.model_name || ""} onChange={(e) => update("model_name", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <Field label="Application"><input value={formData.application || ""} onChange={(e) => update("application", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            <Field label="Customer Due Date"><input type="date" value={formData.customer_due_date || ""} onChange={(e) => update("customer_due_date", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" /></Field>
            {user?.role === "admin" && (
              <Field label={isEdit ? "Unit (admin can reassign)" : "Unit *"}>
                <select value={formData.unit_id || ""} onChange={(e) => update("unit_id", e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="assembly-unit-select">
                  <option value="">Select unit…</option>
                  {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
                </select>
                {isEdit && (
                  <div className="text-[11px] text-slate-500 mt-1">Changing the unit does NOT renumber the assembly. The number prefix (e.g. DEF-ASM-…) stays as a historical label. Only unit-based dashboard scoping and RBAC are affected.</div>
                )}
              </Field>
            )}
          </Section>

          <Section
            title="Project Scope"
            subtitle="How many years is this assembly expected to run, and what are the customer-projected annual volumes? YoY price reduction (if any) is set lower down — it's optional."
          >
            <Field label="Project Life (years)">
              <input type="number" step="1" min="1" max="20"
                value={(formData.project_life_years ?? 1) === 0 || (formData.project_life_years ?? 1) === "0" ? "" : (formData.project_life_years ?? 1)}
                onChange={(e) => updateProjectLife(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="asm-project-life" placeholder="1" />
              <div className="text-[11px] text-slate-500 mt-1">Even one-off assemblies get a 1-year schedule. The volumes table renders below.</div>
            </Field>
            <Field label="SOP Year (Y1 calendar)">
              <input
                value={(formData.volumes && formData.volumes[0] && formData.volumes[0].year_label) || ""}
                onChange={(e) => updateVolumeRow(0, { year_label: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="e.g. 2027" data-testid="asm-sop-year" />
              <div className="text-[11px] text-slate-500 mt-1">Type the year SOP begins (e.g. 2027). Y2..Yn auto-fill if you leave them blank.</div>
            </Field>

            <div className="col-span-2 mt-2">
              <div className="text-xs font-medium text-slate-600 mb-2">Year-by-Year Schedule</div>
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden" data-testid="asm-project-volumes-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-16">Year</th>
                    <th className="text-left p-2 w-24">Calendar</th>
                    <th className="text-right p-2">Annual Vol</th>
                    <th className="text-right p-2">Monthly Vol</th>
                    <th className="text-right p-2">Price/pc{showYoy && Number(formData.yoy_reduction_pct || 0) > 0 ? " (YoY)" : ""}</th>
                    <th className="text-right p-2">Annual Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(formData.volumes || []).map((v, idx) => {
                    const rate = (formData.yoy_reduction_pct || 0) / 100;
                    const cap = formData.yoy_apply_years && Number(formData.yoy_apply_years) > 0 ? Number(formData.yoy_apply_years) : null;
                    const effN = cap === null || v.year_number <= cap + 1 ? v.year_number : cap + 1;
                    const price = (calc.grand_total || 0) * Math.pow(1 - rate, effN - 1);
                    const value = price * (v.annual_volume_pcs || 0);
                    return (
                      <tr key={v.year_number}>
                        <td className="p-2 font-mono text-slate-500">Y{v.year_number}</td>
                        <td className="p-2">
                          <input value={v.year_label || ""}
                                 onChange={(e) => updateVolumeRow(idx, { year_label: e.target.value })}
                                 placeholder={idx === 0 ? "SOP yr" : "—"}
                                 className="w-full px-2 py-1 border border-slate-200 rounded text-sm" />
                        </td>
                        <td className="p-2">
                          <input type="number" step="1" min="0"
                                 value={(v.annual_volume_pcs || 0) === 0 || (v.annual_volume_pcs || 0) === "0" ? "" : (v.annual_volume_pcs || 0)}
                                 onChange={(e) => updateVolumeRow(idx, { annual_volume_pcs: parseFloat(e.target.value) || 0 })}
                                 className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right" placeholder="0" />
                        </td>
                        <td className="p-2">
                          <input type="number" step="1" min="0"
                                 value={(v.monthly_volume_pcs || 0) === 0 || (v.monthly_volume_pcs || 0) === "0" ? "" : (v.monthly_volume_pcs || 0)}
                                 onChange={(e) => updateVolumeRow(idx, { monthly_volume_pcs: parseFloat(e.target.value) || 0 })}
                                 className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right" placeholder="0" />
                        </td>
                        <td className="p-2 text-right font-mono">{formatINR(price)}</td>
                        <td className="p-2 text-right font-mono font-semibold">{formatINR(value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100">
                  <tr>
                    <td colSpan={5} className="p-2 font-semibold text-right">TOTAL PROJECT VALUE</td>
                    <td className="p-2 text-right font-mono font-bold">
                      {formatINR((formData.volumes || []).reduce((s, v) => {
                        const rate = (formData.yoy_reduction_pct || 0) / 100;
                        const cap = formData.yoy_apply_years && Number(formData.yoy_apply_years) > 0 ? Number(formData.yoy_apply_years) : null;
                        const effN = cap === null || v.year_number <= cap + 1 ? v.year_number : cap + 1;
                        const price = (calc.grand_total || 0) * Math.pow(1 - rate, effN - 1);
                        return s + price * (v.annual_volume_pcs || 0);
                      }, 0))}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="text-[11px] text-slate-500 mt-2">Editing annual auto-fills monthly (÷12) and vice versa. Price/pc reflects YoY discount if you've set one below.</p>
            </div>
          </Section>

          <Section id="asm-components" title="Sub-Parts (Components)" subtitle="Link existing parts — or nest another assembly ([ASM]). Each sub-part contributes its Grand Total × qty to the assembly cost.">
            <div className="col-span-2">
              <table className="w-full text-sm border border-slate-200 rounded overflow-visible">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-8">#</th>
                    <th className="text-left p-2">NPD Part</th>
                    <th className="text-right p-2 w-20">Qty</th>
                    <th className="text-right p-2 w-28">₹/pc</th>
                    <th className="text-right p-2 w-28">Line ₹</th>
                    <th className="text-left p-2 w-40">Notes</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(formData.components || []).length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-sm text-slate-400">No sub-parts. Click "Add sub-part" below.</td></tr>
                  )}
                  {(formData.components || []).map((c, idx) => {
                    const line = calc.component_lines[idx] || {};
                    return (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-1.5 text-slate-400 font-mono text-xs text-center">{idx + 1}</td>
                        <td className="p-1.5">
                          <SearchableSelect
                            value={c.npd_id}
                            onChange={(v) => updateComponent(idx, { npd_id: v })}
                            options={npdSelectOptions}
                            placeholder="Search & pick NPD or assembly…"
                            testid={`asm-component-${idx}`}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                          />
                          {/* Show the chosen part/assembly's number below (the box leads with the name). */}
                          {c.npd_id && (() => {
                            const isAsm = String(c.npd_id).startsWith("asm_");
                            const sel = (componentNpds || []).find((n) => n.npd_id === c.npd_id)
                              || npdPickerOptions.find((n) => n.npd_id === c.npd_id);
                            return sel ? (
                              <div className="text-[11px] text-slate-500 mt-0.5 truncate font-mono" title={`${sel.npd_number} — ${sel.part_name || ""}${sel.part_number ? ` (${sel.part_number})` : ""}`}>
                                {isAsm && <span className="text-purple-600 font-semibold">ASM · </span>}{sel.npd_number}{sel.part_number ? ` · ${sel.part_number}` : ""}
                              </div>
                            ) : null;
                          })()}
                          {line.missing && c.npd_id && (
                            <div className="text-[11px] text-red-600 mt-1">⚠ Selected {String(c.npd_id).startsWith("asm_") ? "assembly" : "NPD"} not found or deleted</div>
                          )}
                        </td>
                        <td className="p-1.5">
                          <DecimalInput value={c.qty_per_assembly} onChange={(v) => updateComponent(idx, { qty_per_assembly: v })} className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right" />
                        </td>
                        <td className="p-1.5 text-right font-mono text-xs">{formatINR(line.unit_price || 0)}</td>
                        <td className="p-1.5 text-right font-mono font-semibold">{formatINR(line.line_total || 0)}</td>
                        <td className="p-1.5">
                          <input type="text" value={c.notes || ""} onChange={(e) => updateComponent(idx, { notes: e.target.value })}
                                 className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                                 placeholder="e.g. left side" />
                        </td>
                        <td className="p-1.5 text-right whitespace-nowrap">
                          {c.npd_id && (
                            <a href={`${String(c.npd_id).startsWith("asm_") ? "/assemblies" : "/npd"}/${c.npd_id}`} target="_blank" rel="noreferrer"
                               className="text-sky-600 hover:text-sky-800 mr-2 inline-block align-middle"
                               title="Edit this sub-part (opens in a new tab)">
                              <Pencil size={15} />
                            </a>
                          )}
                          <button onClick={() => moveComponent(idx, -1)} disabled={idx === 0}
                                  className="text-slate-300 hover:text-orange-600 disabled:opacity-30 mr-1"><ArrowUp size={13} /></button>
                          <button onClick={() => moveComponent(idx, 1)} disabled={idx === (formData.components || []).length - 1}
                                  className="text-slate-300 hover:text-orange-600 disabled:opacity-30 mr-1"><ArrowDown size={13} /></button>
                          <button onClick={() => removeComponent(idx)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100">
                  <tr>
                    <td colSpan={4} className="p-2 font-semibold text-right">Components Total</td>
                    <td className="p-2 text-right font-mono font-bold">{formatINR(calc.components_total)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
              <button type="button" onClick={addComponent} className="mt-2 text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1" data-testid="asm-add-component">
                <Plus size={14} /> Add sub-part
              </button>
            </div>
          </Section>

          <Section title="Development Cost (one-time)" subtitle="One-time tooling/development. Sub-parts' own tooling is summed automatically (parts that amortize tooling into their ₹/pc are skipped to avoid double-counting). Shown separately on the quote — not in the per-piece Grand Total.">
            <div className="col-span-2 grid grid-cols-3 gap-3 items-end">
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5">From sub-parts (auto)</label>
                <div className="px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm font-mono text-right text-slate-700" data-testid="asm-dev-from-subparts">
                  {formatINR(calc.components_development_cost || 0)}
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5">Assembly-level (₹, one-time)</label>
                <GroupedNumberInput value={formData.assembly_development_cost ?? 0} onChange={(v) => update("assembly_development_cost", v || 0)} className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right" data-testid="asm-dev-extra" placeholder="0" />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-0.5">Total development cost</label>
                <div className="px-2 py-1.5 bg-orange-50 border border-orange-200 rounded text-sm font-mono text-right font-bold text-orange-700" data-testid="asm-dev-total">
                  {formatINR(calc.development_cost_total || 0)}
                </div>
              </div>
            </div>
          </Section>

          <Section id="asm-process" title="B'. Assembly Process Cost" subtitle="Welding, painting, assembly labor, packaging, etc.">
            <ItemListEditor
              items={formData.assembly_processes}
              onChange={(items) => update("assembly_processes", items)}
              columns={[
                { key: "name", label: "Process", type: "text" },
                { key: "cost", label: "₹/pc", type: "number", width: "w-28 text-right" },
              ]}
              totalLabel="Total Assembly Process Cost (B')"
              total={calc.assembly_process_cost}
              addLabel="Add assembly process"
            />
          </Section>

          <Section id="asm-bop" title="C'. Assembly Bought Out Parts" subtitle="Extra fasteners, gaskets, packaging that don't belong to any sub-part NPD.">
            <ItemListEditor
              items={formData.assembly_bop_items}
              onChange={(items) => update("assembly_bop_items", items)}
              columns={[
                { key: "name", label: "Item", type: "text" },
                { key: "qty", label: "Qty", type: "number", width: "w-20" },
                { key: "unit_price", label: "₹/unit", type: "number", width: "w-28 text-right" },
              ]}
              totalLabel="Total Assembly BOP Cost (C')"
              total={calc.assembly_bop_cost}
              addLabel="Add assembly BOP"
            />
          </Section>

          <Section id="asm-treatment" title="D'. Assembly Treatment & Maintenance" subtitle="Surface/heat treatments applied to the FULL ASSEMBLY (₹/pc).">
            <TreatmentField
              label="Assembly Surface Treatment"
              value={formData.assembly_surface_treatment}
              onChange={(v) => update("assembly_surface_treatment", v)}
              netWeightKg={0}
              placeholderNotes="e.g. powder coating"
            />
            <TreatmentField
              label="Assembly Heat Treatment"
              value={formData.assembly_heat_treatment}
              onChange={(v) => update("assembly_heat_treatment", v)}
              netWeightKg={0}
              placeholderNotes="e.g. stress relief"
            />
            <div className="col-span-2 border border-slate-200 rounded-md p-3 bg-white" data-testid="asm-tool-maintenance-field">
              <div className="flex justify-between items-start mb-2">
                <label className="text-sm font-semibold text-slate-800">Assembly Tool Maintenance</label>
                <div className="text-right">
                  <div className="text-2xl font-bold text-orange-600 leading-tight font-mono">{formatINR(calc.assembly_tool_maintenance)}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">/pc</div>
                </div>
              </div>

              {/* R48 — Mode toggle */}
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="text-slate-500">Mode:</span>
                <button
                  type="button"
                  onClick={() => update("assembly_tool_maintenance_overridden", false)}
                  className={`px-2.5 py-1 rounded border ${
                    !formData.assembly_tool_maintenance_overridden
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid="asm-tool-maint-mode-pct"
                >
                  % of Assembly Process Cost
                </button>
                <button
                  type="button"
                  onClick={() => update("assembly_tool_maintenance_overridden", true)}
                  className={`px-2.5 py-1 rounded border ${
                    formData.assembly_tool_maintenance_overridden
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid="asm-tool-maint-mode-lumpsum"
                >
                  Fixed ₹/pc (lumpsum)
                </button>
              </div>

              {!formData.assembly_tool_maintenance_overridden ? (
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-0.5">% of Assembly Process Cost (B&apos;)</label>
                    <DecimalInput value={formData.assembly_tool_maintenance_pct ?? 0} onChange={(v) => update("assembly_tool_maintenance_pct", v)} className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right" data-testid="asm-tool-maint-pct" />
                  </div>
                  <div className="text-[11px] text-slate-500 pb-1.5 font-mono">
                    = {Number(formData.assembly_tool_maintenance_pct || 0).toFixed(1)}% × {formatINR(calc.assembly_process_cost)}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-0.5">Fixed amount (₹ per piece)</label>
                    <div className="relative">
                      <DecimalInput value={formData.assembly_tool_maintenance_fixed ?? 0} onChange={(v) => update("assembly_tool_maintenance_fixed", v)} className="w-full px-2 py-1 pl-6 border border-slate-200 rounded text-sm font-mono text-right" data-testid="asm-tool-maint-fixed" />
                      <span className="absolute left-2 top-1.5 text-xs text-slate-400 pointer-events-none">₹</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 pb-1.5 font-mono">
                    Fixed lumpsum — independent of Assembly Process Cost
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section id="asm-overheads" title="E'. Assembly Overheads & Margin" subtitle="Applied on top of the sub-total. Basis options include Components Total and Sub-Total.">
            <div className="col-span-2">
              <OverheadsTable
                overheads={formData.assembly_overheads}
                onChange={(o) => update("assembly_overheads", o)}
                calculations={calc.overheads_calculated}
              />
            </div>
          </Section>

          <Section
            title="F. Custom Adjustments"
            subtitle="Customer markups, discounts, or fixed surcharges applied to Assembly Grand Total. All rows use the same base (Grand Total before adjustments) and don't compound."
          >
            <div className="col-span-2" data-testid="asm-custom-adjustments-editor">
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2">Label</th>
                    <th className="text-left p-2 w-40">Mode</th>
                    <th className="text-right p-2 w-28">Value</th>
                    <th className="text-right p-2 w-32">₹/pc</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(formData.assembly_custom_adjustments || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-sm text-slate-400">
                        No custom adjustments. Click &quot;Add adjustment&quot; below.
                      </td>
                    </tr>
                  )}
                  {(formData.assembly_custom_adjustments || []).map((row, idx) => {
                    const mode = row.mode || "pct";
                    const value = Number(row.value) || 0;
                    const gtBeforeAdj = Number(calc.grand_total_before_adj) || 0;
                    const amount = mode === "pct" ? gtBeforeAdj * (value / 100) : value;
                    return (
                      <tr key={idx}>
                        <td className="p-1.5 text-slate-400 font-mono text-xs text-center">{idx + 1}</td>
                        <td className="p-1.5">
                          <input
                            type="text"
                            value={row.label || ""}
                            onChange={(e) => {
                              const next = [...formData.assembly_custom_adjustments];
                              next[idx] = { ...next[idx], label: e.target.value };
                              update("assembly_custom_adjustments", next);
                            }}
                            placeholder="e.g. TACO 5% markup"
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                            data-testid={`asm-adj-label-${idx}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <select
                            value={mode}
                            onChange={(e) => {
                              const next = [...formData.assembly_custom_adjustments];
                              next[idx] = { ...next[idx], mode: e.target.value };
                              update("assembly_custom_adjustments", next);
                            }}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                            data-testid={`asm-adj-mode-${idx}`}
                          >
                            <option value="pct">% of Grand Total</option>
                            <option value="lumpsum">₹ per piece</option>
                          </select>
                        </td>
                        <td className="p-1.5">
                          <DecimalInput
                            value={value}
                            onChange={(v) => {
                              const next = [...formData.assembly_custom_adjustments];
                              next[idx] = { ...next[idx], value: v };
                              update("assembly_custom_adjustments", next);
                            }}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                            data-testid={`asm-adj-value-${idx}`}
                            title={mode === "pct" ? "Percent — negative allowed (discount)" : "Lumpsum ₹/pc — negative allowed (discount)"}
                          />
                        </td>
                        <td className="p-1.5 font-mono text-right text-slate-700">
                          {amount >= 0 ? "+ " : "− "}{formatINR(Math.abs(amount))}
                        </td>
                        <td className="p-1.5 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              const next = formData.assembly_custom_adjustments.filter((_, i) => i !== idx);
                              update("assembly_custom_adjustments", next);
                            }}
                            className="text-slate-300 hover:text-red-500"
                            title="Remove"
                            data-testid={`asm-adj-remove-${idx}`}
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="border-t border-slate-200 p-2 flex justify-between items-center bg-slate-50">
                <button
                  type="button"
                  onClick={() => update("assembly_custom_adjustments", [
                    ...(formData.assembly_custom_adjustments || []),
                    { label: "", mode: "pct", value: 0 },
                  ])}
                  className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                  data-testid="asm-adj-add-btn"
                >
                  <Plus size={14} /> Add adjustment
                </button>
                <div className="text-sm">
                  <span className="text-slate-500">Total Section F:</span>{" "}
                  <strong className="font-mono text-slate-900">
                    {(Number(calc.assembly_custom_adjustments_total) || 0) >= 0 ? "+ " : "− "}
                    {formatINR(Math.abs(Number(calc.assembly_custom_adjustments_total) || 0))} / pc
                  </strong>
                </div>
              </div>

              <div className="mt-3 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="asm-adj-visibility-toggle-row">
                <input
                  type="checkbox"
                  id="asm-adj-visible"
                  checked={!!formData.assembly_custom_adjustments_visible_in_quote}
                  onChange={(e) => update("assembly_custom_adjustments_visible_in_quote", e.target.checked)}
                  className="mt-0.5"
                  data-testid="asm-adj-visible-toggle"
                />
                <label htmlFor="asm-adj-visible" className="text-sm text-amber-900 leading-snug cursor-pointer">
                  <span className="font-semibold">Show adjustments in customer quote</span>
                  <div className="text-xs text-amber-800/80 mt-0.5">
                    When OFF (default), customer sees only the final Grand Total — the adjustment lines are hidden from the PDF and Excel export.
                    When ON, the customer sees each adjustment row and total in their quote.
                  </div>
                </label>
              </div>
            </div>
          </Section>

          <div className="mb-3 flex items-center gap-2">
            <input
              type="checkbox"
              id="asm-show-yoy"
              checked={showYoy}
              onChange={(e) => {
                const next = e.target.checked;
                setShowYoy(next);
                if (!next) {
                  // Unticking clears the discount so the schedule above flattens to GT.
                  setFormData((p) => ({ ...p, yoy_reduction_pct: 0, yoy_apply_years: null }));
                }
              }}
              className="w-4 h-4 cursor-pointer"
              data-testid="asm-show-yoy-toggle"
            />
            <label htmlFor="asm-show-yoy" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
              Apply year-over-year price reduction
            </label>
          </div>
          {showYoy && (
          <Section title="YoY Price Reduction" subtitle="Customer-agreed annual price discount. The Year-by-Year Schedule above will reflect the discounted prices.">
            <Field label="YoY Reduction (% per year, compound)">
              <DecimalInput value={formData.yoy_reduction_pct ?? 0} onChange={(v) => update("yoy_reduction_pct", v)} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="asm-yoy-pct" />
            </Field>
            <Field label="YoY Apply Years (cap, optional)">
              <input type="number" step="1" min="1" max="20"
                     value={(formData.yoy_apply_years ?? "") === 0 || (formData.yoy_apply_years ?? "") === "0" ? "" : (formData.yoy_apply_years ?? "")}
                     onChange={(e) => {
                       const v = e.target.value;
                       update("yoy_apply_years", v === "" ? null : (parseInt(v) || null));
                     }}
                     placeholder="No cap"
                     className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="asm-yoy-apply-years" />
              <div className="text-[11px] text-slate-500 mt-1">Number of times the discount is applied. E.g. 3 = discount applied in Y2, Y3, Y4, then Y5+ stays the same. Leave blank for no cap.</div>
            </Field>

            <div className="col-span-2 mt-2">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <div className="text-xs font-medium text-slate-600">YoY Price Schedule</div>
                {(() => {
                  const rate = (formData.yoy_reduction_pct || 0) / 100;
                  const cap = formData.yoy_apply_years && Number(formData.yoy_apply_years) > 0 ? Number(formData.yoy_apply_years) : null;
                  const total = (formData.volumes || []).reduce((sum, v) => {
                    const effN = cap === null || v.year_number <= cap + 1 ? v.year_number : cap + 1;
                    const price = (calc.grand_total || 0) * Math.pow(1 - rate, effN - 1);
                    return sum + price * (v.annual_volume_pcs || 0);
                  }, 0);
                  return (
                    <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 font-medium" data-testid="yoy-total-revenue-pill">
                      Total Project Revenue: <span className="font-mono font-bold">{formatINR(total)}</span>
                    </div>
                  );
                })()}
              </div>
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden" data-testid="asm-yoy-price-schedule">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-16">Year</th>
                    <th className="text-left p-2 w-24">Calendar</th>
                    <th className="text-right p-2">Price/pc</th>
                    <th className="text-right p-2">Δ vs Y1</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(formData.volumes || []).map((v) => {
                    const rate = (formData.yoy_reduction_pct || 0) / 100;
                    const cap = formData.yoy_apply_years && Number(formData.yoy_apply_years) > 0 ? Number(formData.yoy_apply_years) : null;
                    const effN = cap === null || v.year_number <= cap + 1 ? v.year_number : cap + 1;
                    const isCapped = cap !== null && v.year_number > cap + 1;
                    const price = (calc.grand_total || 0) * Math.pow(1 - rate, effN - 1);
                    const deltaPct = v.year_number === 1 ? 0 : ((price / (calc.grand_total || 1)) - 1) * 100;
                    return (
                      <tr key={v.year_number}>
                        <td className="p-2 font-mono text-slate-500">Y{v.year_number}</td>
                        <td className="p-2">{v.year_label || "—"}</td>
                        <td className="p-2 text-right font-mono font-semibold">{formatINR(price)}</td>
                        <td className="p-2 text-right font-mono text-xs">
                          {v.year_number === 1 ? <span className="text-slate-400">base</span> : (
                            <span className={isCapped ? "text-slate-400" : "text-emerald-600"}>
                              {deltaPct.toFixed(2)}%{isCapped ? " (capped)" : ""}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-500 mt-2">
                Year-on-year unit price after applying YoY reduction. Included in the customer-facing preview and Excel export.
              </p>
            </div>
          </Section>
          )}

          <Section title="Remarks">
            <div className="col-span-2">
              <textarea rows={3} value={formData.remarks || ""} onChange={(e) => update("remarks", e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" placeholder="Internal notes…" />
            </div>
          </Section>

          {isEdit && (
            <Section
              title="Drawing Screenshot"
              subtitle="Mandatory JPG/PNG (max 2 MB) — embedded inside Section A 'COMPONENTS' on the customer quote sheet & Excel export."
            >
              <ScreenshotUpload
                scope="assembly"
                entityId={id}
                path={formData.assembly_drawing_screenshot_path}
                uploadedAt={formData.assembly_drawing_screenshot_uploaded_at}
                sizeBytes={formData.assembly_drawing_screenshot_size_bytes}
                canEdit={true}
                onChanged={(d) => {
                  setFormData((p) => ({
                    ...p,
                    assembly_drawing_screenshot_path: d?.assembly_drawing_screenshot_path || null,
                    assembly_drawing_screenshot_uploaded_at: d?.assembly_drawing_screenshot_uploaded_at || null,
                    assembly_drawing_screenshot_size_bytes: d?.assembly_drawing_screenshot_size_bytes || null,
                  }));
                }}
              />
            </Section>
          )}

          {isEdit && (
            <MiscFilesPanel
              entityType="assemblies"
              entityId={id}
              files={formData.misc_files || []}
              canEdit={true}
              onChange={async () => {
                const r = await api.get(`/assemblies/${id}`);
                setFormData((p) => ({ ...p, misc_files: r.data.misc_files || [] }));
              }}
            />
          )}

          {isEdit && rounds.length > 0 && (() => {
            const CURRENT_KEY = "_current_";
            const leftId = comparingRound?.left || rounds[rounds.length - 1].round_id;
            const rightId = comparingRound?.right || CURRENT_KEY;
            const resolveSide = (id) => {
              if (id === CURRENT_KEY) return { label: "Current", asm: formData, components: componentNpds, hero: undefined };
              const r = rounds.find((x) => x.round_id === id);
              if (!r) return { label: "?", asm: {}, components: [] };
              return { label: r.round_label, asm: r.snapshot?.assembly || {}, components: r.snapshot?.components || [], hero: r.snapshot?.assembly_grand_total_at_save, raw: r };
            };
            const leftSide = resolveSide(leftId);
            const rightSide = resolveSide(rightId);
            const sameSelected = leftId === rightId;
            const setSide = (which, id) => setComparingRound({
              left: which === "left" ? id : leftId,
              right: which === "right" ? id : rightId,
            });
            const leftRaw = leftSide.raw;
            return (
              <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="asm-rounds-section">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><History size={16} /> Negotiation Rounds ({rounds.length})</h3>

                <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Compare any two</div>
                  <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="asm-compare-picker">
                    <select value={leftId} onChange={(e) => setSide("left", e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="asm-compare-left">
                      <option value={CURRENT_KEY}>Current (live)</option>
                      {rounds.map((r) => (<option key={r.round_id} value={r.round_id}>{r.round_label} · {new Date(r.round_date).toLocaleDateString("en-IN")}</option>))}
                    </select>
                    <span className="text-slate-500 text-xs">vs</span>
                    <select value={rightId} onChange={(e) => setSide("right", e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="asm-compare-right">
                      <option value={CURRENT_KEY}>Current (live)</option>
                      {rounds.map((r) => (<option key={r.round_id} value={r.round_id}>{r.round_label} · {new Date(r.round_date).toLocaleDateString("en-IN")}</option>))}
                    </select>
                    {sameSelected && <span className="text-[11px] text-amber-600">Pick two different sides</span>}
                  </div>
                  {!sameSelected && (
                    <AssemblyCompareView
                      round={{
                        round_id: leftId,
                        round_label: leftSide.label,
                        round_date: leftRaw?.round_date || new Date().toISOString(),
                        notes: leftRaw?.notes || "",
                        created_by_name: leftRaw?.created_by_name || "",
                        snapshot: {
                          assembly: leftSide.asm,
                          components: leftSide.components,
                          assembly_grand_total_at_save: leftSide.hero || 0,
                        },
                      }}
                      currentAsm={rightSide.asm}
                      currentComponents={rightSide.components}
                      rightLabel={rightSide.label}
                      assemblyId={id}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  {rounds.map((r) => {
                    const isShortcut = leftId === r.round_id && rightId === CURRENT_KEY;
                    const heroTotal = r.snapshot?.assembly_grand_total_at_save || 0;
                    return (
                      <div key={r.round_id} className={`border rounded-md p-3 ${isShortcut ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <div className="min-w-0 flex items-center gap-2 flex-wrap">
                            <span className="inline-block text-xs font-bold bg-sky-100 text-sky-700 px-2 py-0.5 rounded">{r.round_label}</span>
                            <span className="text-xs text-slate-500">{new Date(r.round_date).toLocaleString("en-IN")}</span>
                            {r.created_by_name && <span className="text-xs text-slate-400">· by {r.created_by_name}</span>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-sm font-mono font-semibold text-slate-900">{formatINR(heroTotal)}</div>
                            <button
                              onClick={() => setComparingRound({ left: r.round_id, right: CURRENT_KEY })}
                              className={`text-xs px-2 py-1 rounded border whitespace-nowrap ${isShortcut ? "bg-sky-600 text-white border-sky-600" : "border-sky-500 text-sky-700 hover:bg-sky-50"}`}
                              data-testid={`asm-compare-round-${r.round_label}`}
                              title="Quick compare this round vs Current"
                            >
                              vs Current
                            </button>
                          </div>
                        </div>
                        {r.notes && <div className="text-xs text-slate-600 mt-1">{r.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="lg:order-last">
          <AssemblyLiveCalcPanel calc={calc} threshold={threshold} hideMargin={hideMargin}
            entityId={isEdit ? id : null} screenshotPath={formData.assembly_drawing_screenshot_path} />
        </div>
      </div>

      {pdfAsmId && <AssemblyPdfDownloader assemblyId={pdfAsmId} onDone={() => setPdfAsmId(null)} />}
    </div>
  );
}

function Section({ title, subtitle, children, id }) {
  return (
    <div id={id} className="bg-white border border-slate-200 rounded-lg p-5 scroll-mt-4 transition-shadow">
      <div className="mb-4">
        <h3 className="font-semibold text-base text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ItemListEditor({ items, onChange, columns, totalLabel, total, addLabel }) {
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, columns.reduce((acc, c) => ({ ...acc, [c.key]: c.type === "number" ? 0 : "" }), {})]);
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  return (
    <div className="col-span-2">
      <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left p-2 w-8">#</th>
            {columns.map((c) => <th key={c.key} className={`text-left p-2 ${c.width || ""}`}>{c.label}</th>)}
            <th className="w-20"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.length === 0 && (
            <tr><td colSpan={columns.length + 2} className="p-6 text-center text-sm text-slate-400">No items. Click "Add" below.</td></tr>
          )}
          {items.map((it, idx) => (
            <tr key={idx} className="hover:bg-slate-50">
              <td className="p-1.5 text-slate-400 font-mono text-xs text-center">{idx + 1}</td>
              {columns.map((c) => (
                <td key={c.key} className="p-1.5">
                  <input type={c.type} step={c.type === "number" ? "0.0001" : undefined}
                         value={it[c.key] ?? (c.type === "number" ? 0 : "")}
                         onChange={(e) => update(idx, { [c.key]: c.type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value })}
                         className={`w-full px-2 py-1 border border-slate-200 rounded text-sm ${c.type === "number" ? "font-mono text-right" : ""}`} />
                </td>
              ))}
              <td className="p-1.5 text-right whitespace-nowrap">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-300 hover:text-orange-600 disabled:opacity-30 mr-1"><ArrowUp size={13} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-slate-300 hover:text-orange-600 disabled:opacity-30 mr-1"><ArrowDown size={13} /></button>
                <button onClick={() => remove(idx)} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between mt-2">
        <button type="button" onClick={add} className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"><Plus size={14} /> {addLabel}</button>
        {totalLabel && <div className="text-sm"><span className="text-slate-500">{totalLabel}:</span> <strong className="font-mono">{formatINR(total)}</strong></div>}
      </div>
    </div>
  );
}
